import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_PREFIX = "lvb_drawer_width_";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export default function useDrawerResize(storageKey, defaultWidth, minWidth, maxWidth) {
  const [width, setWidth] = useState(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_PREFIX + storageKey);
      const parsed = stored ? parseInt(stored, 10) : NaN;
      if (!Number.isNaN(parsed)) return clamp(parsed, minWidth, maxWidth);
    } catch (err) {
      // ignore unavailable storage
    }
    return defaultWidth;
  });

  const widthRef = useRef(width);
  const cleanupRef = useRef(null);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const stopDragging = useCallback(() => {
    if (cleanupRef.current) {
      window.removeEventListener("mousemove", cleanupRef.current.onMove);
      window.removeEventListener("mouseup", cleanupRef.current.onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      cleanupRef.current = null;
    }
  }, []);

  const onDragStart = useCallback(
    (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      stopDragging();

      const startX = event.clientX;
      const startWidth = widthRef.current;

      const onMove = (moveEvent) => {
        const next = startWidth - (moveEvent.clientX - startX);
        setWidth(clamp(next, minWidth, maxWidth));
      };

      const onUp = () => {
        const finalWidth = widthRef.current;
        try {
          window.localStorage.setItem(STORAGE_PREFIX + storageKey, String(Math.round(finalWidth)));
        } catch (err) {
          // ignore unavailable storage
        }
        stopDragging();
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      cleanupRef.current = { onMove, onUp };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [maxWidth, minWidth, storageKey, stopDragging]
  );

  useEffect(() => stopDragging, [stopDragging]);

  return { width, onDragStart };
}
